import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner, Textarea } from '@noor/ui';
import { Can } from '@/entities/access';
import type { ModerationEntityType } from '@/entities/moderation';
import {
  rejectRequestSchema,
  useApproveChangeRequest,
  useRejectChangeRequest,
} from '@/features/moderation-actions';

interface InlineDecisionFooterProps {
  entityType: ModerationEntityType;
  changeRequestId: string;
  approveLabel: string;
  rejectLabel: string;
  onDone: () => void;
}

/**
 * Decision footer of the single-column review (Figma 616:19215 footer): an
 * inline comment (required to reject — the API demands a reason) above the
 * decline / approve pill row.
 */
export function InlineDecisionFooter({
  entityType,
  changeRequestId,
  approveLabel,
  rejectLabel,
  onDone,
}: InlineDecisionFooterProps) {
  const { t } = useTranslation('moderation');
  const [comment, setComment] = useState('');
  const approve = useApproveChangeRequest(entityType);
  const reject = useRejectChangeRequest(entityType);

  const busy = approve.isPending || reject.isPending;
  const rejectValid = rejectRequestSchema.safeParse({ reason: comment }).success;

  return (
    <>
      <div className="flex flex-col gap-2">
        <label
          htmlFor={`reject-comment-${changeRequestId}`}
          className="text-sm leading-[1.4] font-medium text-foreground"
        >
          {t('create.comment', 'Комментарий')}{' '}
          <span className="font-normal text-muted-foreground">
            ({t('create.commentHint', 'Обязательно при отклонении')})
          </span>
        </label>
        <Textarea
          id={`reject-comment-${changeRequestId}`}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          placeholder={t('create.commentPlaceholder', 'Введите причину отклонения')}
          rows={3}
          maxLength={500}
          className="min-h-[88px] resize-none rounded-[20px] bg-muted p-[15px] text-sm shadow-none"
        />
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        <Can permission={`moderation.${entityType}.reject`}>
          <Button
            type="button"
            variant="soft"
            shape="rounded"
            size="modal"
            onClick={() => {
              if (busy || !rejectValid) return;
              reject.mutate(
                { changeRequestId, reason: comment.trim() },
                { onSuccess: onDone },
              );
            }}
            disabled={busy || !rejectValid}
            aria-busy={reject.isPending}
          >
            {reject.isPending && <Spinner size="sm" className="text-current" />}
            {rejectLabel}
          </Button>
        </Can>
        <Can permission={`moderation.${entityType}.approve`}>
          <Button
            type="button"
            variant="dark"
            shape="rounded"
            size="modal"
            onClick={() => {
              if (busy) return;
              approve.mutate({ changeRequestId }, { onSuccess: onDone });
            }}
            disabled={busy}
            aria-busy={approve.isPending}
          >
            {approve.isPending && <Spinner size="sm" className="text-current" />}
            {approveLabel}
          </Button>
        </Can>
      </div>
    </>
  );
}
